// src/components/forms/variants/gated/types.ts

import type { ReactNode } from 'react'
import { siteConfig } from '~/lib/config/site'
import type { 
  BaseFormProps, 
  ContentConfig, 
  ThemeConfig,
  ImageConfig,
  FeatureListItemConfig,
  PrivacyConfig,
  BadgeConfig,
  SubtitleConfig,
  EmailTemplateConfig
} from '~/components/forms/base/types'

/**
 * Configuration for additional resource details
 */
export interface ResourceDetailItem {
  icon: ReactNode
  text: string
}

/**
 * Configuration for resource details
 */
export interface ResourceDetails {
  /** Type of resource (e.g., "PDF", "Video", etc.) */
  type: string
  /** Title of the resource */
  title?: string
  /** Description of the resource */
  description?: string
  /** Preview image URL */
  previewImage?: string
  /** Custom icon for the resource */
  icon?: ReactNode
  /** Number of pages (for documents) */
  pages?: number
  /** File format */
  format?: string
  /** File size */
  size?: string
  /** Additional details with custom icons */
  additionalDetails?: ResourceDetailItem[]
}

/**
 * Gated-specific theme configuration
 */
export interface GatedThemeConfig extends ThemeConfig {
  /** Preview section styles */
  preview?: {
    className?: string
    style?: React.CSSProperties
  }
}

/**
 * Props for the GatedContentEmailCapture component
 */
export interface GatedContentEmailCaptureProps extends Omit<BaseFormProps, 'content' | 'theme'> {
  /** Resource details configuration */
  resourceDetails: ResourceDetails
  
  /** Content configuration extending base content */
  content: ContentConfig & {
    /** Optional preview image configuration */
    preview?: ImageConfig
    /** Optional badge configuration */
    badge?: BadgeConfig
    /** Optional subtitle configuration */
    subtitle?: SubtitleConfig
    /** Optional feature list */
    features?: FeatureListItemConfig[]
    /** Optional privacy notice configuration */
    privacy?: PrivacyConfig
  }
  
  /** Theme configuration extending base theme */
  theme?: GatedThemeConfig
  
  /** Email template configuration */
  template?: EmailTemplateConfig
}

/**
 * Default preset configuration
 */
export const GATED_PRESET: Partial<GatedContentEmailCaptureProps> = {
  theme: {
    variant: 'gated',
    layout: 'stacked',
    size: 'lg',
    theme: 'light',
    className: {
      container: 'max-w-2xl mx-auto',
      form: 'rounded-xl bg-white dark:bg-gray-900 shadow-xl'
    }
  },
  content: {
    title: {
      text: 'Get Access to the Resource',
      className: 'text-2xl font-bold tracking-tight'
    },
    description: {
      text: 'Enter your email to unlock this exclusive content.',
      className: 'text-gray-600 dark:text-gray-400'
    },
    input: {
      placeholder: 'Enter your work email'
    },
    button: {
      text: 'Get Access',
      loadingText: 'Processing...'
    },
    messages: {
      success: 'Thank you! Check your email to access the resource.',
      error: 'Please enter a valid email address.'
    },
    privacy: {
      enabled: true,
      text: 'By continuing, you agree to our',
      privacyLink: {
        text: 'Privacy Policy',
        url: `${siteConfig.baseLinks.privacy}`
      },
      termsLink: {
        text: 'Terms of Service',
        url: `${siteConfig.baseLinks.terms}` 
      }
    }
  }
} as const 